import { useParams, Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const relatedArticles = useMemo(() => (article ? getRelatedArticles(article) : []), [article]);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 亚洲一级日本精选内容导航`;

      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach((s) => {
        if (s.textContent?.includes('"@type":"Article"')) {
          s.remove();
        }
      });

      const articleSchema = document.createElement('script');
      articleSchema.type = 'application/ld+json';
      articleSchema.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        image: article.image,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.date,
        publisher: {
          '@type': 'Organization',
          name: '亚洲一级日本精选内容导航',
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://kaxifa.cn/article/${article.slug}`,
        },
      });
      document.head.appendChild(articleSchema);

      const breadcrumbSchema = document.createElement('script');
      breadcrumbSchema.type = 'application/ld+json';
      breadcrumbSchema.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://kaxifa.cn/' },
          { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://kaxifa.cn/category/${article.categoryId}` },
          { '@type': 'ListItem', position: 3, name: article.title, item: `https://kaxifa.cn/article/${article.slug}` },
        ],
      });
      document.head.appendChild(breadcrumbSchema);
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen">
          <div className="container-main text-center py-20">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 min-h-screen">
        <article className="container-main">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 overflow-x-auto flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line"></i>
            </span>
            <Link to={`/category/${article.categoryId}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">
              {article.categoryName}
            </Link>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line"></i>
            </span>
            <span className="text-foreground-800 font-medium truncate max-w-[200px]">{article.title}</span>
          </nav>

          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <span className="inline-block px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-md mb-3">
                {article.categoryName}
              </span>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-xs text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-user-line"></i>
                  </span>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-calendar-line"></i>
                  </span>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-time-line"></i>
                  </span>
                  阅读约{article.readTime}
                </span>
              </div>
            </div>

            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-base max-w-none text-foreground-700 article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full border border-background-200/70"
                >
                  {tag}
                </span>
              ))}
            </div>

            {relatedArticles.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200/70">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">相关文章推荐</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.slug}`}
                      className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-300"
                    >
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={related.image}
                          alt={related.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-3">
                        <h3 className="text-sm font-heading font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors mb-1">
                          {related.title}
                        </h3>
                        <span className="text-xs text-foreground-400">{related.date}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}